import React, { Fragment, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { cartActions } from "../../../store/cart"; // Adjust the path as necessary
import { orderActions } from "../../../store/order";
import zest from "../../../assets/images/zest.png";
import icon from "../../../assets/images/i-logo.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { TbStarFilled } from "react-icons/tb";
import { TbStarHalfFilled } from "react-icons/tb";
import { ImStarEmpty } from "react-icons/im";
import SizeChart from "./SizeChart";
import Alert from "@mui/material/Alert";
import { Snackbar } from "@mui/material";
import { baseURL, formatPrice } from "../../../utils";
import Loader from "../../Common/Loader";
import axios from "axios";
import ReactImageMagnify from "react-image-magnify";
import { Helmet } from "react-helmet";
import logo from "../../../assets/images/logo-updated.png";

const SIZE_TO_CM_MAP = {
  "2x3": "60 x 90",
  "3x5": "90 x 150",
  "4x6": "120 x 180",
  "5x8": "150 x 240",
  "6x9": "180 x 270",
  "8x10": "240 x 300",
  "9x12": "270 x 360",
  "10x14": "300 x 420",
  "12x15": "360 x 450",
};

const normalizeSizeKey = (name = "") =>
  name
    .toLowerCase()
    .replace(/feet|foot|ft|cm|["']/g, "") // remove unit words
    .replace(/×/g, "x") // handle multiplication sign
    .replace(/[^0-9]+/g, "x") // turn all non-digits into separators
    .replace(/^x+|x+$/g, "") // trim leading/trailing separators
    .replace(/x+/g, "x") // collapse multiple separators
    .trim();

const convertToCmFromNormalized = (normalized) => {
  if (!normalized.includes("x")) return null;
  const parts = normalized
    .split("x")
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));

  if (parts.length !== 2) return null;

  const toCm = (ft) => Math.round(ft * 30); // matches provided rounded values
  const [widthFt, heightFt] = parts;
  return `${toCm(widthFt)} x ${toCm(heightFt)}`;
};

const getSizeInCm = (name, fallback) => {
  const candidates = [name, fallback].filter(Boolean);

  for (const label of candidates) {
    const normalized = normalizeSizeKey(label);
    if (normalized) {
      if (SIZE_TO_CM_MAP[normalized]) {
        return SIZE_TO_CM_MAP[normalized];
      }
      const calculated = convertToCmFromNormalized(normalized);
      if (calculated) {
        return calculated;
      }
    }
  }

  return null;
};

const ProductInfo = ({ productDetails }) => {
  const [mainImage, setMainImage] = useState(
    productDetails ? productDetails.images[0].url : ""
  );
  const [quantity, setQuantity] = useState(1);
  const [isShareopen, setShareOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [showViewCart, setShowViewCart] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pincode, setPincode] = useState("");
  const [isAvailable, setIsAvailable] = useState(false);
  const [error, setError] = useState("");
  const [errorColor, setErrorColor] = useState("red");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const cart = useSelector((state) => state.cart.cart); // Access the cart state from Redux
  const orders = useSelector((state) => state.order.order);
  // Check if the product is already in the cart
  const isInCart = cart.some((item) => item._id === productDetails._id);

  const handleSetMainImage = (image) => {
    setMainImage(image);
  };

  const handleAddToCart = () => {
    setIsLoading(true);
    dispatch(
      cartActions.addToCart({
        ...productDetails,
        quantity,
        selectedSize,
      })
    );
    const currentCart = JSON.parse(localStorage.getItem("cart")) || [];
    const updatedCart = [
      ...currentCart,
      { ...productDetails, quantity, selectedSize },
    ];
    const gtmItems = updatedCart.map((cartItem) => ({
      item_name: cartItem.name,
      item_id: cartItem._id,
      price: cartItem.priceSale || cartItem.price || 0,

      item_variant: cartItem.selectedSize
        ? cartItem.selectedSize.name
        : "Default",
      quantity: cartItem.quantity,
    }));

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        currency: "INR", // Change to your currency
        items: gtmItems, // Include all items in the cart
      },
    });

    setTimeout(() => {
      setIsLoading(false); // Hide loader
      setShowViewCart(true); // Show "View Cart" button
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 2000); // Loader duration (2 seconds)

    // Hide "View Cart" button after 4 seconds
    setTimeout(() => {
      setShowViewCart(false);
    }, 6000); // Additional time for the "View Cart" button to be visible
  };

  const handleRemoveFromCart = () => {
    dispatch(cartActions.removeFromCart(productDetails._id)); // Remove using the product ID
  };

  // Update localStorage whenever the cart changes
  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);
  useEffect(() => {
    const savedOrder = JSON.parse(localStorage.getItem("order")) || [];
    if (savedOrder.length > 0) {
      dispatch(orderActions.setOrder(savedOrder));
    }
  }, [dispatch]);

  useEffect(() => {
    localStorage.setItem("order", JSON.stringify(orders));
  }, [orders]);
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    const sizeSlug = urlParams.get("size");
    setSelectedSize(sizeSlug);
    if (sizeSlug && productDetails) {
      // Find the full size object based on slug
      const foundSize = productDetails.sizes.find(
        (size) => size.slug === sizeSlug
      );

      if (foundSize) {
        console.log(foundSize);
        setSelectedSize(foundSize); // Set the entire size object
      }
    }
  }, [location.search]);

  const handleViewCart = () => {
    navigate("/cart");
  };
  const handleBuyNow = (e, item) => {
    e.stopPropagation();

    // Dispatch the action to add the item to the order in Redux

    dispatch(orderActions.addOrder({ ...item, quantity, sizes: selectedSize }));
    dispatch(cartActions.addToCart({ ...item, quantity, selectedSize }));
    // Save the order to localStorage
    const updatedOrder = [{ ...item, quantity }];
    const updatedCart = [...cart, { ...item, quantity, sizes: selectedSize }];
    localStorage.setItem("order", JSON.stringify(updatedOrder));
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    localStorage.setItem("dataComingFrom", "buy-now");

    const gtmItems = [
      {
        item_name: item.name,
        item_id: item._id,
        price: item.priceSale || item.price || 0,
        item_variant: selectedSize ? selectedSize.name : "Default",
        quantity: quantity,
      },
    ];

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "begin_checkout",
      ecommerce: {
        currency: "INR", // Use your site's currency
        items: gtmItems,
        value: item.priceSale ?? item.price,
      },
    });

    // Navigate to the checkout page
    // navigate("/checkout");
    window.location.href = "/checkout";
  };
  const handleShareOpen = () => {
    setShareOpen(!isShareopen);
  };
  // Function to render stars based on the rating
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0; // Check for half star
    const stars = [];

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <span key={i} className="filled-star">
            <TbStarFilled />
          </span>
        );
      } else if (hasHalfStar && i === fullStars) {
        stars.push(
          <span key={i} className="half-star">
            <TbStarHalfFilled />
          </span>
        );
      } else {
        stars.push(
          <span key={i} className="empty-star">
            <ImStarEmpty />
          </span>
        );
      }
    }

    return <div className="star-rating">{stars}</div>;
  };
  const handleOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };
  const handleColorRedirect = (item) => {
    // console.log(item);
    // console.log(item);
    window.location.href = `/product/${item.slug}?size=${
      item.sizes[0].size ? item.sizes[0].size : item.sizes[0].slug
    }`;
  };
  const currentUrl = `${
    window.location.href
  }?cachebuster=${new Date().getTime()}`;

  const handleShare = (platform) => {
    let shareUrl = "";
    const currentUrl = `${
      window.location.href
    }?cachebuster=${new Date().getTime()}`;

    switch (platform) {
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          currentUrl
        )}`;
        break;
      case "instagram":
        shareUrl = `https://www.instagram.com/?url=${encodeURIComponent(
          currentUrl
        )}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=Check%20this%20out&body=I%20found%20this%20interesting:%20${encodeURIComponent(
          currentUrl
        )}`;
        break;
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodeURIComponent(currentUrl)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, "_blank"); // Open the share link in a new tab
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };
  const copyToClipboard = () => {
    const link = window.location.href;
    navigator.clipboard
      .writeText(link)
      .then(() => {
        setSnackbarOpen(true);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };
  const handleSizeSelect = (size) => {
    if (size) {
      setSelectedSize(size);
      navigate(`/product/${productDetails.slug}?size=${size.slug}`);
    }
  };
  const areEqual = (objA, objB) => {
    return JSON.stringify(objA) === JSON.stringify(objB);
  };
  const handleKeyPress = (e) => {
    if (!/^\d$/.test(e.key)) {
      e.preventDefault(); // Prevent non-numeric characters
    }
  };
  const handleChange = async (e) => {
    const input = e.target.value;

    // Set the pincode
    setPincode(input);
    setIsAvailable(false);
    // Reset error if user starts typing again
    setError("");

    // Check if the pincode length is less than 6
    if (input.length < 6) {
      setError("Pincode must be 6 digits long");
      return;
    }

    // If pincode length is exactly 6, make the API call

    if (input.length === 6) {
      try {
        // Simulate loading state
        // setIsLoading(true);

        const body = {
          pincode: input,
        };
        // Replace with your API endpoint
        const response = await axios.post(`${baseURL}/check-pincode`, body);

        if (response.data.status === true) {
          // setError(response.data.message);
          // setErrorColor("green");
          setIsAvailable(true);
        } else {
          setIsAvailable(false);
          setError(response.data.message);
          setErrorColor("red");
        }

        // Add any additional logic to handle the response
      } catch (error) {
        console.error("Error fetching pincode details:", error);
        setIsAvailable(false);
        setError("Failed to validate pincode. Please try again.");
        setErrorColor("red");
      }
    }
  };

  return (
    <Fragment>
      <Helmet>
        <title>{productDetails ? productDetails.name : "Rug"}</title>
        <meta
          property="og:title"
          content={productDetails ? productDetails.name : "Rug"}
        />
        <meta
          property="og:description"
          content={
            productDetails ? productDetails.description : "Rug Description"
          }
        />
        <meta
          property="og:image"
          content={productDetails ? productDetails.images[0].url : logo}
        />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000} // Automatically close after 2 seconds
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success">
          Link copied to clipboard!
        </Alert>
      </Snackbar>
      <div class="breadcrumb">
        <a href="/">Home</a> &nbsp;&nbsp;/&nbsp;&nbsp;
        <a href="/rugs">Shop</a>&nbsp;&nbsp; /&nbsp;&nbsp;
        <span>
          {productDetails
            ? productDetails.name
                .toLowerCase()
                .replace(/\b\w/g, (char) => char.toUpperCase())
            : ""}
        </span>
      </div>
      {showViewCart && (
        <div className="viewCartBoxContainer">
          <div className="addToCartBox">
            <p className="boxText">
              "
              {productDetails
                ? productDetails.name
                    .toLowerCase()
                    .replace(/\b\w/g, (char) => char.toUpperCase())
                : ""}
              " has been added to cart.
            </p>
            <button className="viewCartBox" onClick={handleViewCart}>
              View Cart
            </button>
          </div>
        </div>
      )}

      <div className="prodContainer">
        <div className="product-title-mob">
          {" "}
          <h1 className="product-title-mob-text">
            {productDetails.name
              .toLowerCase()
              .replace(/\b\w/g, (char) => char.toUpperCase())}
          </h1>
          <div
            className="share-prod-mob"
            style={{ cursor: "pointer" }}
            onClick={handleShareOpen}
          >
            <svg
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M20.7625 26.2157C19.9342 26.2157 19.2313 25.9265 18.6538 25.3481C18.0771 24.7688 17.7887 24.0656 17.7887 23.2383C17.7887 23.1135 17.8438 22.7985 17.9537 22.2933L8.9575 16.9591C8.6875 17.2703 8.35708 17.5146 7.96625 17.6918C7.57542 17.8691 7.15583 17.9577 6.7075 17.9577C5.88583 17.9577 5.1875 17.6656 4.6125 17.0814C4.0375 16.4972 3.75 15.7968 3.75 14.9804C3.75 14.164 4.0375 13.4636 4.6125 12.8794C5.1875 12.2952 5.88583 12.003 6.7075 12.003C7.155 12.003 7.57458 12.0917 7.96625 12.269C8.35792 12.4462 8.68833 12.6909 8.9575 13.003L17.955 7.6912C17.8967 7.52974 17.8546 7.36954 17.8287 7.21058C17.8021 7.05079 17.7887 6.88767 17.7887 6.72122C17.7887 5.8948 18.0792 5.19197 18.66 4.61273C19.2408 4.03432 19.9458 3.74512 20.775 3.74512C21.6042 3.74512 22.3075 4.03515 22.885 4.61523C23.4625 5.1953 23.7508 5.89938 23.75 6.72746C23.7492 7.55554 23.4596 8.25796 22.8813 8.8347C22.3029 9.41144 21.5987 9.6994 20.7687 9.69857C20.3171 9.69857 19.9004 9.60619 19.5187 9.42143C19.1371 9.23667 18.8121 8.98825 18.5437 8.67616L9.545 14.0104C9.60333 14.1719 9.64542 14.3325 9.67125 14.4923C9.69792 14.6512 9.71125 14.814 9.71125 14.9804C9.71125 15.1468 9.69792 15.3096 9.67125 15.4685C9.64458 15.6275 9.60292 15.7881 9.54625 15.9504L18.5437 21.2846C18.8129 20.9725 19.1379 20.7241 19.5187 20.5394C19.9004 20.3546 20.3171 20.2622 20.7687 20.2622C21.5971 20.2622 22.3013 20.5519 22.8813 21.1311C23.4604 21.712 23.75 22.4165 23.75 23.2446C23.75 24.0727 23.4596 24.7751 22.8787 25.3518C22.2979 25.9286 21.5917 26.2165 20.7625 26.2157ZM20.7687 24.9673C21.2596 24.9673 21.6708 24.8017 22.0025 24.4705C22.3342 24.1392 22.5 23.7289 22.5 23.2396C22.5 22.7502 22.3342 22.3395 22.0025 22.0074C21.6708 21.6754 21.26 21.5098 20.77 21.5106C20.28 21.5114 19.8687 21.677 19.5362 22.0074C19.2037 22.3378 19.0379 22.7481 19.0387 23.2383C19.0396 23.7285 19.2054 24.1392 19.5362 24.4705C19.8671 24.8017 20.2771 24.9673 20.7687 24.9673ZM6.7075 16.7081C7.20417 16.7081 7.62083 16.5425 7.9575 16.2113C8.29333 15.8801 8.46125 15.4698 8.46125 14.9804C8.46125 14.491 8.29333 14.0807 7.9575 13.7495C7.62167 13.4183 7.205 13.2527 6.7075 13.2527C6.22333 13.2527 5.81792 13.4183 5.49125 13.7495C5.16458 14.0807 5.00083 14.491 5 14.9804C4.99917 15.4698 5.16292 15.8805 5.49125 16.2125C5.81958 16.5446 6.225 16.7098 6.7075 16.7081ZM20.77 8.4502C21.26 8.4502 21.6708 8.28459 22.0025 7.95335C22.3342 7.62212 22.5 7.21141 22.5 6.72122C22.5 6.23103 22.3342 5.82073 22.0025 5.49033C21.6708 5.15993 21.26 4.99431 20.77 4.99348C20.28 4.99265 19.8687 5.15827 19.5362 5.49033C19.2037 5.8224 19.0379 6.23311 19.0387 6.72247C19.0396 7.21183 19.2054 7.62212 19.5362 7.95335C19.8671 8.28459 20.2783 8.4502 20.77 8.4502Z"
                fill="black"
              />
            </svg>
            {isShareopen && (
              <div className="share-prod-list">
                <ul>
                  <li onClick={() => handleShare("facebook")}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.65">
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M8.48583 18.2084V10.8184H6V7.93844H8.48583V5.81844C8.43585 5.31519 8.49632 4.8071 8.663 4.32964C8.82969 3.85218 9.09858 3.41685 9.4509 3.05405C9.80322 2.69126 10.2305 2.40973 10.7029 2.22913C11.1752 2.04854 11.6813 1.97322 12.1858 2.00844C12.9284 2.00406 13.6706 2.04413 14.4083 2.12844V4.69844H12.8833C12.6897 4.65737 12.4889 4.66483 12.2989 4.72016C12.1088 4.7755 11.9354 4.87698 11.7941 5.01558C11.6528 5.15418 11.5479 5.32558 11.4889 5.51451C11.4299 5.70344 11.4185 5.90405 11.4558 6.09844V7.93844H14.3083L13.9358 10.8184H11.4608V18.2084H8.48583Z"
                          fill="#303030"
                        />
                      </g>
                    </svg>
                  </li>
                  <li onClick={() => handleShare("instagram")}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.65">
                        <path
                          fill-rule="evenodd"
                          clip-rule="evenodd"
                          d="M10 2C7.8275 2 7.555 2.00917 6.70167 2.04833C5.85 2.08667 5.26833 2.2225 4.76 2.42C4.22631 2.62083 3.74286 2.93564 3.34333 3.3425C2.9362 3.74196 2.62109 4.22542 2.42 4.75917C2.2225 5.26833 2.08667 5.85083 2.04833 6.70167C2.00833 7.555 2 7.82667 2 10C2 12.1725 2.00917 12.445 2.04833 13.2983C2.08667 14.15 2.2225 14.7317 2.42 15.24C2.62085 15.7737 2.93566 16.2571 3.3425 16.6567C3.7875 17.1025 4.23417 17.3758 4.75917 17.58C5.26833 17.7775 5.85 17.9133 6.70083 17.9517C7.555 17.9908 7.8275 18 10 18C12.1725 18 12.445 17.9908 13.2983 17.9517C14.15 17.9133 14.7317 17.7775 15.24 17.58C15.7737 17.3792 16.2571 17.0643 16.6567 16.6575C17.0638 16.258 17.3789 15.7746 17.58 15.2408C17.7775 14.7317 17.9133 14.1492 17.9517 13.2992C17.9908 12.445 18 12.1725 18 10C18 7.8275 17.9908 7.555 17.9517 6.70167C17.9125 5.85 17.7775 5.26833 17.58 4.76C17.3792 4.22633 17.0643 3.74287 16.6575 3.34333C16.2581 2.93618 15.7746 2.62107 15.2408 2.42C14.7317 2.2225 14.1492 2.08667 13.2983 2.04833C12.445 2.00833 12.1733 2 10 2ZM10 3.44167C12.1358 3.44167 12.3892 3.45 13.2333 3.48833C14.0125 3.52333 14.4358 3.65417 14.7183 3.76333C15.0917 3.90833 15.3583 4.08167 15.6383 4.36167C15.9183 4.64167 16.0917 4.90833 16.2367 5.28167C16.3458 5.56333 16.4758 5.9875 16.5117 6.7675C16.55 7.61083 16.5583 7.86417 16.5583 10C16.5583 12.1358 16.55 12.3892 16.5117 13.2333C16.4758 14.0125 16.3458 14.4367 16.2367 14.7183C16.1082 15.0659 15.9035 15.3803 15.6375 15.6383C15.3796 15.9039 15.0656 16.1084 14.7183 16.2367C14.4358 16.3458 14.0125 16.4767 13.2325 16.5117C12.3892 16.55 12.1358 16.5583 9.99917 16.5583C7.86333 16.5583 7.61083 16.55 6.7675 16.5117C5.9875 16.4767 5.56333 16.3458 5.28167 16.2367C4.93414 16.1085 4.61977 15.904 4.36167 15.6383C4.09598 15.3802 3.89152 15.0659 3.76333 14.7183C3.65417 14.4367 3.52333 14.0125 3.48833 13.2325C3.44917 12.3892 3.44167 12.1358 3.44167 10C3.44167 7.86417 3.45 7.61083 3.48833 6.76667C3.52333 5.9875 3.65417 5.56417 3.76333 5.28167C3.90833 4.90833 4.08167 4.64167 4.36167 4.36167C4.61977 4.09598 4.93414 3.89152 5.28167 3.76333C5.56333 3.65417 5.9875 3.52333 6.7675 3.48833C7.61083 3.45 7.86417 3.44167 10 3.44167ZM10 12.6667C9.29276 12.6667 8.61448 12.3857 8.11438 11.8856C7.61428 11.3855 7.33333 10.7072 7.33333 10C7.33333 9.29276 7.61428 8.61448 8.11438 8.11438C8.61448 7.61428 9.29276 7.33333 10 7.33333C10.7072 7.33333 11.3855 7.61428 11.8856 8.11438C12.3857 8.61448 12.6667 9.29276 12.6667 10C12.6667 10.7072 12.3857 11.3855 11.8856 11.8856C11.3855 12.3857 10.7072 12.6667 10 12.6667ZM10 5.89167C9.46049 5.89167 8.92625 5.99793 8.42781 6.20439C7.92936 6.41086 7.47646 6.71348 7.09497 7.09497C6.71348 7.47646 6.41086 7.92936 6.20439 8.42781C5.99793 8.92625 5.89167 9.46049 5.89167 10C5.89167 10.5395 5.99793 11.0737 6.20439 11.5722C6.41086 12.0706 6.71348 12.5235 7.09497 12.905C7.47646 13.2865 7.92936 13.5891 8.42781 13.7956C8.92625 14.0021 9.46049 14.1083 10 14.1083C11.0896 14.1083 12.1346 13.6755 12.905 12.905C13.6755 12.1346 14.1083 11.0896 14.1083 10C14.1083 8.9104 13.6755 7.86543 12.905 7.09497C12.1346 6.32451 11.0896 5.89167 10 5.89167ZM15.23 5.73C15.23 5.98461 15.1289 6.22879 14.9488 6.40882C14.7688 6.58886 14.5246 6.69 14.27 6.69C14.0154 6.69 13.7712 6.58886 13.5912 6.40882C13.4111 6.22879 13.31 5.98461 13.31 5.73C13.31 5.47539 13.4111 5.23121 13.5912 5.05118C13.7712 4.87114 14.0154 4.77 14.27 4.77C14.5246 4.77 14.7688 4.87114 14.9488 5.05118C15.1289 5.23121 15.23 5.47539 15.23 5.73Z"
                          fill="#303030"
                        />
                      </g>
                    </svg>
                  </li>
                  <li>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => handleShare("email")}
                    >
                      <path
                        d="M3.33317 16.6666C2.87484 16.6666 2.48262 16.5035 2.1565 16.1774C1.83039 15.8513 1.66706 15.4588 1.6665 14.9999V4.99992C1.6665 4.54159 1.82984 4.14936 2.1565 3.82325C2.48317 3.49714 2.87539 3.33381 3.33317 3.33325H16.6665C17.1248 3.33325 17.5173 3.49659 17.844 3.82325C18.1707 4.14992 18.3337 4.54214 18.3332 4.99992V14.9999C18.3332 15.4583 18.1701 15.8508 17.844 16.1774C17.5179 16.5041 17.1254 16.6671 16.6665 16.6666H3.33317ZM9.99984 10.8333L3.33317 6.66659V14.9999H16.6665V6.66659L9.99984 10.8333ZM9.99984 9.16658L16.6665 4.99992H3.33317L9.99984 9.16658ZM3.33317 6.66659V4.99992V14.9999V6.66659Z"
                        fill="#787878"
                      />
                    </svg>
                  </li>
                  <li onClick={() => handleShare("whatsapp")}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.8752 4.09155C15.1111 3.31997 14.201 2.70819 13.1981 2.29185C12.1952 1.87551 11.1194 1.66295 10.0335 1.66655C5.4835 1.66655 1.77516 5.37488 1.77516 9.92488C1.77516 11.3832 2.1585 12.7999 2.87516 14.0499L1.7085 18.3332L6.0835 17.1832C7.29183 17.8416 8.65016 18.1915 10.0335 18.1915C14.5835 18.1915 18.2918 14.4832 18.2918 9.93322C18.2918 7.72488 17.4335 5.64988 15.8752 4.09155ZM10.0335 16.7915C8.80016 16.7915 7.59183 16.4582 6.5335 15.8332L6.2835 15.6832L3.6835 16.3665L4.37516 13.8332L4.2085 13.5749C3.52312 12.4808 3.15927 11.2159 3.1585 9.92488C3.1585 6.14155 6.24183 3.05822 10.0252 3.05822C11.8585 3.05822 13.5835 3.77488 14.8752 5.07488C15.5148 5.71144 16.0218 6.46867 16.3665 7.30266C16.7113 8.13664 16.887 9.03079 16.8835 9.93322C16.9002 13.7165 13.8168 16.7915 10.0335 16.7915ZM13.8002 11.6582C13.5918 11.5582 12.5752 11.0582 12.3918 10.9832C12.2002 10.9165 12.0668 10.8832 11.9252 11.0832C11.7835 11.2915 11.3918 11.7582 11.2752 11.8916C11.1585 12.0332 11.0335 12.0499 10.8252 11.9415C10.6168 11.8415 9.95016 11.6166 9.16683 10.9165C8.55016 10.3665 8.14183 9.69155 8.01683 9.48322C7.90016 9.27488 8.00016 9.16655 8.1085 9.05822C8.20016 8.96655 8.31683 8.81655 8.41683 8.69988C8.51683 8.58322 8.5585 8.49155 8.62516 8.35822C8.69183 8.21655 8.6585 8.09988 8.6085 7.99988C8.5585 7.89988 8.14183 6.88322 7.97516 6.46655C7.8085 6.06655 7.6335 6.11655 7.5085 6.10822H7.1085C6.96683 6.10822 6.75016 6.15822 6.5585 6.36655C6.37516 6.57488 5.84183 7.07488 5.84183 8.09155C5.84183 9.10822 6.5835 10.0915 6.6835 10.2249C6.7835 10.3666 8.14183 12.4499 10.2085 13.3415C10.7002 13.5582 11.0835 13.6832 11.3835 13.7749C11.8752 13.9332 12.3252 13.9082 12.6835 13.8582C13.0835 13.7999 13.9085 13.3582 14.0752 12.8749C14.2502 12.3915 14.2502 11.9832 14.1918 11.8916C14.1335 11.7999 14.0085 11.7582 13.8002 11.6582Z"
                        fill="#787878"
                      />
                    </svg>
                  </li>
                  <li onClick={copyToClipboard}>
                    {" "}
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M13.3235 10.8333L16.5752 7.5816C17.7727 6.3841 17.8127 4.48327 16.6644 3.33577C15.5169 2.18744 13.616 2.22744 12.4185 3.42494L9.16687 6.6766M10.8335 13.3016L7.5902 16.5349C6.39687 17.7266 4.55687 17.8808 3.3552 16.6241C2.15354 15.3683 2.2502 13.5916 3.44437 12.4008L6.6877 9.1666M7.5002 12.4999L12.5002 7.49994"
                        stroke="#787878"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
        <div className="left-column">
          <div className="thumbnail-gallery">
            {productDetails.images.map((item) => (
              <img
                key={item.url}
                src={item.url}
                alt="Thumbnail"
                onClick={() => handleSetMainImage(item.url)}
              />
            ))}
          </div>
          <div className="main-image">
            {/* <img src={mainImage} alt="Main Product Image" /> */}
            <ReactImageMagnify
              {...{
                smallImage: {
                  alt: "Main Product Image",
                  isFluidWidth: true,
                  src: mainImage,
                },
                largeImage: {
                  src: mainImage,
                  width: 1200,
                  height: 1700,
                },
                enlargedImageContainerStyle: {
                  position: "absolute",
                  top: "0",
                  left: "100%",
                  marginLeft: "30px",
                  zIndex: 30,
                  width: "100%",
                  height: "100%",
                  transform: "translateX(10px)",
                  // overflow: "hidden",
                  background: "#FFFFFF",
                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                },
                enlargedImageContainerDimensions: {
                  width: "150%",
                  height: "100%",
                },
                lensStyle: {
                  background: "rgba(255,255,255,0.3)",
                  border: "1px solid #ccc",
                },
                isHintEnabled: true,
                hoverDelayInMs: 0,
                hoverOffDelayInMs: 0,
                shouldUsePositiveSpaceLens: true,
              }}
            />
          </div>
        </div>

        <div className="right-column">
          <div className="ship-share-box">
            <div className="ship-box">Fast Shipping</div>
            <div
              className="share-prod"
              style={{ cursor: "pointer" }}
              onClick={handleShareOpen}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20.7625 26.2157C19.9342 26.2157 19.2313 25.9265 18.6538 25.3481C18.0771 24.7688 17.7887 24.0656 17.7887 23.2383C17.7887 23.1135 17.8438 22.7985 17.9537 22.2933L8.9575 16.9591C8.6875 17.2703 8.35708 17.5146 7.96625 17.6918C7.57542 17.8691 7.15583 17.9577 6.7075 17.9577C5.88583 17.9577 5.1875 17.6656 4.6125 17.0814C4.0375 16.4972 3.75 15.7968 3.75 14.9804C3.75 14.164 4.0375 13.4636 4.6125 12.8794C5.1875 12.2952 5.88583 12.003 6.7075 12.003C7.155 12.003 7.57458 12.0917 7.96625 12.269C8.35792 12.4462 8.68833 12.6909 8.9575 13.003L17.955 7.6912C17.8967 7.52974 17.8546 7.36954 17.8287 7.21058C17.8021 7.05079 17.7887 6.88767 17.7887 6.72122C17.7887 5.8948 18.0792 5.19197 18.66 4.61273C19.2408 4.03432 19.9458 3.74512 20.775 3.74512C21.6042 3.74512 22.3075 4.03515 22.885 4.61523C23.4625 5.1953 23.7508 5.89938 23.75 6.72746C23.7492 7.55554 23.4596 8.25796 22.8813 8.8347C22.3029 9.41144 21.5987 9.6994 20.7687 9.69857C20.3171 9.69857 19.9004 9.60619 19.5187 9.42143C19.1371 9.23667 18.8121 8.98825 18.5437 8.67616L9.545 14.0104C9.60333 14.1719 9.64542 14.3325 9.67125 14.4923C9.69792 14.6512 9.71125 14.814 9.71125 14.9804C9.71125 15.1468 9.69792 15.3096 9.67125 15.4685C9.64458 15.6275 9.60292 15.7881 9.54625 15.9504L18.5437 21.2846C18.8129 20.9725 19.1379 20.7241 19.5187 20.5394C19.9004 20.3546 20.3171 20.2622 20.7687 20.2622C21.5971 20.2622 22.3013 20.5519 22.8813 21.1311C23.4604 21.712 23.75 22.4165 23.75 23.2446C23.75 24.0727 23.4596 24.7751 22.8787 25.3518C22.2979 25.9286 21.5917 26.2165 20.7625 26.2157ZM20.7687 24.9673C21.2596 24.9673 21.6708 24.8017 22.0025 24.4705C22.3342 24.1392 22.5 23.7289 22.5 23.2396C22.5 22.7502 22.3342 22.3395 22.0025 22.0074C21.6708 21.6754 21.26 21.5098 20.77 21.5106C20.28 21.5114 19.8687 21.677 19.5362 22.0074C19.2037 22.3378 19.0379 22.7481 19.0387 23.2383C19.0396 23.7285 19.2054 24.1392 19.5362 24.4705C19.8671 24.8017 20.2771 24.9673 20.7687 24.9673ZM6.7075 16.7081C7.20417 16.7081 7.62083 16.5425 7.9575 16.2113C8.29333 15.8801 8.46125 15.4698 8.46125 14.9804C8.46125 14.491 8.29333 14.0807 7.9575 13.7495C7.62167 13.4183 7.205 13.2527 6.7075 13.2527C6.22333 13.2527 5.81792 13.4183 5.49125 13.7495C5.16458 14.0807 5.00083 14.491 5 14.9804C4.99917 15.4698 5.16292 15.8805 5.49125 16.2125C5.81958 16.5446 6.225 16.7098 6.7075 16.7081ZM20.77 8.4502C21.26 8.4502 21.6708 8.28459 22.0025 7.95335C22.3342 7.62212 22.5 7.21141 22.5 6.72122C22.5 6.23103 22.3342 5.82073 22.0025 5.49033C21.6708 5.15993 21.26 4.99431 20.77 4.99348C20.28 4.99265 19.8687 5.15827 19.5362 5.49033C19.2037 5.8224 19.0379 6.23311 19.0387 6.72247C19.0396 7.21183 19.2054 7.62212 19.5362 7.95335C19.8671 8.28459 20.2783 8.4502 20.77 8.4502Z"
                  fill="black"
                />
              </svg>
              {isShareopen && (
                <div className="share-prod-list">
                  <ul>
                    <li onClick={() => handleShare("facebook")}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.65">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M8.48583 18.2084V10.8184H6V7.93844H8.48583V5.81844C8.43585 5.31519 8.49632 4.8071 8.663 4.32964C8.82969 3.85218 9.09858 3.41685 9.4509 3.05405C9.80322 2.69126 10.2305 2.40973 10.7029 2.22913C11.1752 2.04854 11.6813 1.97322 12.1858 2.00844C12.9284 2.00406 13.6706 2.04413 14.4083 2.12844V4.69844H12.8833C12.6897 4.65737 12.4889 4.66483 12.2989 4.72016C12.1088 4.7755 11.9354 4.87698 11.7941 5.01558C11.6528 5.15418 11.5479 5.32558 11.4889 5.51451C11.4299 5.70344 11.4185 5.90405 11.4558 6.09844V7.93844H14.3083L13.9358 10.8184H11.4608V18.2084H8.48583Z"
                            fill="#303030"
                          />
                        </g>
                      </svg>
                    </li>
                    <li onClick={() => handleShare("instagram")}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g opacity="0.65">
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M10 2C7.8275 2 7.555 2.00917 6.70167 2.04833C5.85 2.08667 5.26833 2.2225 4.76 2.42C4.22631 2.62083 3.74286 2.93564 3.34333 3.3425C2.9362 3.74196 2.62109 4.22542 2.42 4.75917C2.2225 5.26833 2.08667 5.85083 2.04833 6.70167C2.00833 7.555 2 7.82667 2 10C2 12.1725 2.00917 12.445 2.04833 13.2983C2.08667 14.15 2.2225 14.7317 2.42 15.24C2.62085 15.7737 2.93566 16.2571 3.3425 16.6567C3.7875 17.1025 4.23417 17.3758 4.75917 17.58C5.26833 17.7775 5.85 17.9133 6.70083 17.9517C7.555 17.9908 7.8275 18 10 18C12.1725 18 12.445 17.9908 13.2983 17.9517C14.15 17.9133 14.7317 17.7775 15.24 17.58C15.7737 17.3792 16.2571 17.0643 16.6567 16.6575C17.0638 16.258 17.3789 15.7746 17.58 15.2408C17.7775 14.7317 17.9133 14.1492 17.9517 13.2992C17.9908 12.445 18 12.1725 18 10C18 7.8275 17.9908 7.555 17.9517 6.70167C17.9125 5.85 17.7775 5.26833 17.58 4.76C17.3792 4.22633 17.0643 3.74287 16.6575 3.34333C16.2581 2.93618 15.7746 2.62107 15.2408 2.42C14.7317 2.2225 14.1492 2.08667 13.2983 2.04833C12.445 2.00833 12.1733 2 10 2ZM10 3.44167C12.1358 3.44167 12.3892 3.45 13.2333 3.48833C14.0125 3.52333 14.4358 3.65417 14.7183 3.76333C15.0917 3.90833 15.3583 4.08167 15.6383 4.36167C15.9183 4.64167 16.0917 4.90833 16.2367 5.28167C16.3458 5.56333 16.4758 5.9875 16.5117 6.7675C16.55 7.61083 16.5583 7.86417 16.5583 10C16.5583 12.1358 16.55 12.3892 16.5117 13.2333C16.4758 14.0125 16.3458 14.4367 16.2367 14.7183C16.1082 15.0659 15.9035 15.3803 15.6375 15.6383C15.3796 15.9039 15.0656 16.1084 14.7183 16.2367C14.4358 16.3458 14.0125 16.4767 13.2325 16.5117C12.3892 16.55 12.1358 16.5583 9.99917 16.5583C7.86333 16.5583 7.61083 16.55 6.7675 16.5117C5.9875 16.4767 5.56333 16.3458 5.28167 16.2367C4.93414 16.1085 4.61977 15.904 4.36167 15.6383C4.09598 15.3802 3.89152 15.0659 3.76333 14.7183C3.65417 14.4367 3.52333 14.0125 3.48833 13.2325C3.44917 12.3892 3.44167 12.1358 3.44167 10C3.44167 7.86417 3.45 7.61083 3.48833 6.76667C3.52333 5.9875 3.65417 5.56417 3.76333 5.28167C3.90833 4.90833 4.08167 4.64167 4.36167 4.36167C4.61977 4.09598 4.93414 3.89152 5.28167 3.76333C5.56333 3.65417 5.9875 3.52333 6.7675 3.48833C7.61083 3.45 7.86417 3.44167 10 3.44167ZM10 12.6667C9.29276 12.6667 8.61448 12.3857 8.11438 11.8856C7.61428 11.3855 7.33333 10.7072 7.33333 10C7.33333 9.29276 7.61428 8.61448 8.11438 8.11438C8.61448 7.61428 9.29276 7.33333 10 7.33333C10.7072 7.33333 11.3855 7.61428 11.8856 8.11438C12.3857 8.61448 12.6667 9.29276 12.6667 10C12.6667 10.7072 12.3857 11.3855 11.8856 11.8856C11.3855 12.3857 10.7072 12.6667 10 12.6667ZM10 5.89167C9.46049 5.89167 8.92625 5.99793 8.42781 6.20439C7.92936 6.41086 7.47646 6.71348 7.09497 7.09497C6.71348 7.47646 6.41086 7.92936 6.20439 8.42781C5.99793 8.92625 5.89167 9.46049 5.89167 10C5.89167 10.5395 5.99793 11.0737 6.20439 11.5722C6.41086 12.0706 6.71348 12.5235 7.09497 12.905C7.47646 13.2865 7.92936 13.5891 8.42781 13.7956C8.92625 14.0021 9.46049 14.1083 10 14.1083C11.0896 14.1083 12.1346 13.6755 12.905 12.905C13.6755 12.1346 14.1083 11.0896 14.1083 10C14.1083 8.9104 13.6755 7.86543 12.905 7.09497C12.1346 6.32451 11.0896 5.89167 10 5.89167ZM15.23 5.73C15.23 5.98461 15.1289 6.22879 14.9488 6.40882C14.7688 6.58886 14.5246 6.69 14.27 6.69C14.0154 6.69 13.7712 6.58886 13.5912 6.40882C13.4111 6.22879 13.31 5.98461 13.31 5.73C13.31 5.47539 13.4111 5.23121 13.5912 5.05118C13.7712 4.87114 14.0154 4.77 14.27 4.77C14.5246 4.77 14.7688 4.87114 14.9488 5.05118C15.1289 5.23121 15.23 5.47539 15.23 5.73Z"
                            fill="#303030"
                          />
                        </g>
                      </svg>
                    </li>
                    <li onClick={() => handleShare("email")}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3.33317 16.6666C2.87484 16.6666 2.48262 16.5035 2.1565 16.1774C1.83039 15.8513 1.66706 15.4588 1.6665 14.9999V4.99992C1.6665 4.54159 1.82984 4.14936 2.1565 3.82325C2.48317 3.49714 2.87539 3.33381 3.33317 3.33325H16.6665C17.1248 3.33325 17.5173 3.49659 17.844 3.82325C18.1707 4.14992 18.3337 4.54214 18.3332 4.99992V14.9999C18.3332 15.4583 18.1701 15.8508 17.844 16.1774C17.5179 16.5041 17.1254 16.6671 16.6665 16.6666H3.33317ZM9.99984 10.8333L3.33317 6.66659V14.9999H16.6665V6.66659L9.99984 10.8333ZM9.99984 9.16658L16.6665 4.99992H3.33317L9.99984 9.16658ZM3.33317 6.66659V4.99992V14.9999V6.66659Z"
                          fill="#787878"
                        />
                      </svg>
                    </li>
                    <li onClick={() => handleShare("whatsapp")}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M15.8752 4.09155C15.1111 3.31997 14.201 2.70819 13.1981 2.29185C12.1952 1.87551 11.1194 1.66295 10.0335 1.66655C5.4835 1.66655 1.77516 5.37488 1.77516 9.92488C1.77516 11.3832 2.1585 12.7999 2.87516 14.0499L1.7085 18.3332L6.0835 17.1832C7.29183 17.8416 8.65016 18.1915 10.0335 18.1915C14.5835 18.1915 18.2918 14.4832 18.2918 9.93322C18.2918 7.72488 17.4335 5.64988 15.8752 4.09155ZM10.0335 16.7915C8.80016 16.7915 7.59183 16.4582 6.5335 15.8332L6.2835 15.6832L3.6835 16.3665L4.37516 13.8332L4.2085 13.5749C3.52312 12.4808 3.15927 11.2159 3.1585 9.92488C3.1585 6.14155 6.24183 3.05822 10.0252 3.05822C11.8585 3.05822 13.5835 3.77488 14.8752 5.07488C15.5148 5.71144 16.0218 6.46867 16.3665 7.30266C16.7113 8.13664 16.887 9.03079 16.8835 9.93322C16.9002 13.7165 13.8168 16.7915 10.0335 16.7915ZM13.8002 11.6582C13.5918 11.5582 12.5752 11.0582 12.3918 10.9832C12.2002 10.9165 12.0668 10.8832 11.9252 11.0832C11.7835 11.2915 11.3918 11.7582 11.2752 11.8916C11.1585 12.0332 11.0335 12.0499 10.8252 11.9415C10.6168 11.8415 9.95016 11.6166 9.16683 10.9165C8.55016 10.3665 8.14183 9.69155 8.01683 9.48322C7.90016 9.27488 8.00016 9.16655 8.1085 9.05822C8.20016 8.96655 8.31683 8.81655 8.41683 8.69988C8.51683 8.58322 8.5585 8.49155 8.62516 8.35822C8.69183 8.21655 8.6585 8.09988 8.6085 7.99988C8.5585 7.89988 8.14183 6.88322 7.97516 6.46655C7.8085 6.06655 7.6335 6.11655 7.5085 6.10822H7.1085C6.96683 6.10822 6.75016 6.15822 6.5585 6.36655C6.37516 6.57488 5.84183 7.07488 5.84183 8.09155C5.84183 9.10822 6.5835 10.0915 6.6835 10.2249C6.7835 10.3666 8.14183 12.4499 10.2085 13.3415C10.7002 13.5582 11.0835 13.6832 11.3835 13.7749C11.8752 13.9332 12.3252 13.9082 12.6835 13.8582C13.0835 13.7999 13.9085 13.3582 14.0752 12.8749C14.2502 12.3915 14.2502 11.9832 14.1918 11.8916C14.1335 11.7999 14.0085 11.7582 13.8002 11.6582Z"
                          fill="#787878"
                        />
                      </svg>
                    </li>
                    <li onClick={copyToClipboard}>
                      {" "}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.3235 10.8333L16.5752 7.5816C17.7727 6.3841 17.8127 4.48327 16.6644 3.33577C15.5169 2.18744 13.616 2.22744 12.4185 3.42494L9.16687 6.6766M10.8335 13.3016L7.5902 16.5349C6.39687 17.7266 4.55687 17.8808 3.3552 16.6241C2.15354 15.3683 2.2502 13.5916 3.44437 12.4008L6.6877 9.1666M7.5002 12.4999L12.5002 7.49994"
                          stroke="#787878"
                          stroke-width="1.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="prod-title-review">
            <h1 className="product-title">
              {productDetails.name
                .toLowerCase()
                .replace(/\b\w/g, (char) => char.toUpperCase())}
            </h1>
          </div>
          <div className="main-review-rating">
            {renderStars(productDetails.totalRating)}
            <div className="prod-tot-reviews">
              ({productDetails.totalReviews} customer reviews)
            </div>
          </div>
          {productDetails.priceSale ? (
            <p className="price">
              <span className="original-price">
                {formatPrice(productDetails.price)}
              </span>{" "}
              <span className="sale-price">
                {formatPrice(productDetails.priceSale)}
              </span>
              <span className="mrp-note">MRP (Inc. All Taxes)</span>
            </p>
          ) : (
            <p className="price">
              <span>{formatPrice(productDetails.price)}</span>
              <span className="mrp-note">MRP (Inc. All Taxes)</span>
            </p>
          )}
          {/* <p className="price">
            {formatPrice(productDetails.price)}{" "}
            <span>
              Or 3 interest-free payments of 3300 using
              <img src={zest} alt="Zest" />
              <img src={icon} alt="" />
              <span className="tax">(Inclusive of all taxes)</span>
            </span>
          </p> */}
          {productDetails.priceSale ? (
            <p className="price-mob">
              <span className="original-price">
                {formatPrice(productDetails.price)}
              </span>{" "}
              <span className="sale-price">
                {formatPrice(productDetails.priceSale)}
              </span>
              <span className="mrp-note">MRP (Inc. All Taxes)</span>
            </p>
          ) : (
            <p className="price-mob">
              <span>{formatPrice(productDetails.price)}</span>
              <span className="mrp-note">MRP (Inc. All Taxes)</span>
            </p>
          )}
          {/* <p className="price-mob">
            {formatPrice(productDetails.price)}{" "}
            <span>
              <span style={{ fontSize: "10px" }}>
                (Inclusive of all taxes){" "}
              </span>{" "}
              <br />
              Or 3 interest-free payments of 3300 using
              <img src={zest} alt="Zest" />
         
            </span>
          </p> */}
          <div className="rating">
            {renderStars(productDetails.totalRating)}{" "}
            <p>({productDetails.totalReviews} customer reviews)</p>
          </div>
          {/* <p className="description">{productDetails.description}</p>

          <div className="product-details">
            <div className="prod-no-tech">
              <p>
                <span>Product no:</span> {productDetails.code}
              </p>
              <p>
                <span>Technique:</span> {productDetails.technique}
              </p>
            </div>
            <div className="prod-no-tech">
              <p>
                <span>Composition:</span> {productDetails.composition}
              </p>
              <p>
                <span>Design type:</span> {productDetails.category.name}
              </p>
            </div>
          </div> */}

          <div className="color-selection">
            <p>Color</p>
            <div className="color-options">
              <div className="color-image color-image-selected">
                <img
                  src={productDetails.images[0].url}
                  alt="Color 1"
                  onClick={() => handleColorRedirect(productDetails)}
                />
              </div>
              {productDetails.similarColorProducts.map((color) => (
                <div className="color-image">
                  <img
                    src={color.images[0].url}
                    alt="Color 1"
                    onClick={() => handleColorRedirect(color)}
                  />
                </div>
              ))}

              {/* <img src={Color2} alt="Color 2" />
              <img src={Color3} alt="Color 3" />
              <img src={Color4} alt="Color 4" /> */}
            </div>
          </div>

          <div className="size-selection">
            <div className="size-head">
              <p>Size</p>
              <a onClick={handleOpen} style={{ cursor: "pointer" }}>
                Size Chart
              </a>
            </div>
            {productDetails.sizes.map((size) => {
              const sizeLabel = size.name || size.size || size.slug;
              const sizeInCm = getSizeInCm(sizeLabel, size.slug);
              return (
                <button
                  key={size.slug}
                  onClick={() => handleSizeSelect(size)}
                  className={
                    areEqual(selectedSize, size) ? "size-selected" : ""
                  }
                >
                  <span className="size-label">{sizeLabel}</span>
                  {sizeInCm && (
                    <span className="size-cm">{`${sizeInCm} cm`}</span>
                  )}
                </button>
              );
            })}

            {/* <button>5 x 9 ft</button>
            <button>7 x 7 ft</button> */}
          </div>

          <div className="quantity-section">
            <p>Quantity</p>
            <div className="custom-select-wrapper">
              <select
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
                className="custom-select"
              >
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </select>
            </div>
          </div>
          <div className="pincode-check-container">
            <div className="pincode-check">
              <p>Enter pincode to check your estimated delivery date</p>
              <input
                type="text"
                id="pincode-input"
                placeholder="Enter Pincode"
                value={pincode}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                maxLength="6"
              />
            </div>
            {isAvailable && (
              <div className="success-message-pincode">
                <h4>Delivery by 4-5 days</h4>
                <span>If ordered within 30 mins.</span>
              </div>
            )}
            {error && (
              <p
                className="error-message-pincode"
                style={{ color: errorColor }}
              >
                {error}
              </p>
            )}
          </div>
          {productDetails.available <= 0 ? (
            <div className="out-of-stock-btn">
              <button className="out-of-stock" disabled>
                Out Of Stock
              </button>{" "}
            </div>
          ) : (
            <div className="cta-buttons">
              <button
                className="add-to-cart"
                onClick={handleAddToCart}
                disabled={isLoading}
              >
                {isLoading ? <Loader /> : "Add to Cart"}
              </button>

              <button
                className="buy-now-product"
                onClick={(e) => handleBuyNow(e, productDetails)}
              >
                Buy Now
              </button>
            </div>
          )}

          <div className="extra-info">
            <div className="extra-info-item">
              {" "}
              <svg
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.1042 3.1875H4.10417C3.13171 3.1875 2.19907 3.57381 1.51144 4.26144C0.823808 4.94908 0.4375 5.88171 0.4375 6.85417V8.6875M15.1042 7.77083V9.60417C15.1042 10.5766 14.7179 11.5093 14.0302 12.1969C13.3426 12.8845 12.41 13.2708 11.4375 13.2708H0.4375"
                  stroke="black"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12.3542 5.9375L15.1042 3.1875L12.3542 0.4375M3.1875 10.5208L0.4375 13.2708L3.1875 16.0208"
                  stroke="black"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>{" "}
              <span> Easy 7 day exchange</span>
            </div>
            <div className="extra-info-item">
              <svg
                width="20"
                height="22"
                viewBox="0 0 20 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M2.42255 9.74315C2.38289 9.74284 2.34095 9.73695 2.29809 9.72984C2.19688 9.71307 2.09157 9.68548 1.99127 9.66684C1.81165 9.63342 1.64524 9.63438 1.51987 9.70568C1.37854 9.78592 1.08677 9.94193 0.890734 10.0953C0.784966 10.1781 0.70564 10.2642 0.669168 10.3385C0.668712 10.3389 0.646827 10.3853 0.638165 10.4157C0.63087 10.4419 0.629506 10.4652 0.629962 10.4813C0.632241 10.5325 0.653212 10.5683 0.675095 10.5925C0.701993 10.622 0.811406 10.7051 0.928116 10.8007C1.00607 10.8645 1.08996 10.9341 1.13783 10.9952C0.979631 11.2249 0.923105 11.3836 0.94362 11.5192C0.966415 11.6666 1.08404 11.8122 1.34663 12.0047C1.51987 12.1317 1.9548 12.4299 2.31815 12.5454C2.51327 12.6075 2.69244 12.6144 2.82238 12.5562C2.94091 12.5032 3.08771 12.3522 3.23542 12.2136C3.2924 12.1601 3.3503 12.1092 3.40729 12.0741C3.41641 12.0686 3.42508 12.063 3.43374 12.0582C3.45836 12.1213 3.49665 12.2211 3.49938 12.2391C3.57233 12.7029 3.52993 13.1313 3.4971 13.5702C3.462 14.0371 3.43692 14.5151 3.54816 15.0533C3.68265 15.7043 4.0884 16.193 4.34234 16.7948C4.43945 17.0262 4.47501 17.2746 4.51604 17.5221C4.56163 17.7994 4.61315 18.0755 4.73852 18.3324C4.81967 18.4995 4.93501 18.6493 5.04807 18.7983C5.14563 18.9267 5.24229 19.0542 5.30246 19.1982C5.45291 19.5576 5.47753 20.2121 5.6535 20.7084C5.79392 21.1046 6.02643 21.4047 6.42443 21.4722C6.72122 21.5225 6.92911 21.3786 7.09916 21.1705C7.22453 21.0168 7.32437 20.8236 7.44701 20.708C7.62709 20.538 7.92023 20.3712 8.16323 20.1805C8.35151 20.0328 8.51108 19.8701 8.59359 19.6899C8.64009 19.5876 8.64101 19.4487 8.61593 19.2992C8.5904 19.1493 8.53524 18.9849 8.54937 18.8657C8.56943 18.704 8.63736 18.5441 8.70027 18.3848C8.77595 18.1938 8.84479 18.0035 8.85482 17.8156C8.86576 17.6074 8.73492 17.2507 8.65468 16.9508C8.63234 16.868 8.61502 16.7905 8.60955 16.7241C8.60727 16.6936 8.60043 16.6675 8.61137 16.6502C8.7682 16.4066 8.99615 16.2958 9.22775 16.193C9.50904 16.0682 9.79671 15.9545 10.0224 15.7212C10.755 14.9646 11.417 14.1404 12.1405 13.3664C12.2203 13.2814 12.3165 13.2208 12.4117 13.1591C12.5303 13.0821 12.647 13.0039 12.7446 12.8988C12.8444 12.7912 12.879 12.6761 12.8973 12.5593C12.9046 12.5103 12.9087 12.4613 12.9178 12.4135C12.926 12.372 12.9378 12.3309 12.9752 12.2954C13.1343 12.1444 13.355 12.0857 13.5643 12.008C13.8314 11.9089 14.0826 11.7855 14.2417 11.5254C14.3388 11.3664 14.3621 11.1641 14.3297 10.9346C14.2891 10.6465 14.1597 10.3086 14.0343 9.97375C13.9372 9.71594 13.8428 9.46009 13.8041 9.23319C13.7735 9.05438 13.7717 8.89637 13.867 8.78549C13.9212 8.72304 13.9568 8.68474 13.9992 8.68852C14.0471 8.69276 14.0844 8.73539 14.1282 8.77925C14.2217 8.87248 14.316 8.99411 14.4236 9.09213C14.5125 9.17314 14.6105 9.23898 14.7195 9.2744C14.9274 9.34201 15.1462 9.34361 15.3619 9.37032C15.3865 9.37333 15.4175 9.37356 15.4512 9.37237C15.2588 9.79193 15.1211 10.0196 15.3331 10.5878C15.4125 10.8011 15.6035 10.8615 15.8337 10.839C15.9454 10.8281 16.0671 10.7977 16.1711 10.7814C16.1948 10.7777 16.2253 10.776 16.2468 10.7753C16.2509 10.7854 16.2545 10.7955 16.2586 10.8056C16.2737 10.8457 16.2668 10.8321 16.2878 10.886C16.3188 10.9655 16.4601 11.2362 16.6028 11.427C16.6758 11.5245 16.7519 11.6015 16.8148 11.6389C16.885 11.6806 16.952 11.6879 17.0077 11.675C17.0569 11.6637 17.1029 11.6374 17.1408 11.5874C17.3177 11.3545 17.2894 11.1214 17.1882 10.8854C17.1303 10.7515 17.0469 10.6167 16.9867 10.4806C16.9434 10.3822 16.9101 10.284 16.937 10.1835C16.9666 10.074 17.0409 10.0408 17.1194 10.0228C17.1736 10.0105 17.2301 10.0039 17.2848 9.99563C17.4731 9.96709 17.6359 9.90855 17.7207 9.74138C17.7841 9.61655 17.8971 9.38299 17.9482 9.18627C17.976 9.07996 17.9851 8.98167 17.9723 8.90663C17.9546 8.80127 17.8885 8.6754 17.8337 8.55157C17.8178 8.51588 17.8032 8.48041 17.7941 8.44576C17.7868 8.42009 17.7795 8.39579 17.7882 8.37382C17.9318 8.01161 18.4396 7.54513 18.7784 7.34846C18.8613 7.3005 19.0273 7.26936 19.1413 7.20663C19.2853 7.12698 19.3674 7.01027 19.3469 6.85112C19.304 6.52355 19.0063 6.22644 18.6056 6.02703C18.276 5.86291 17.8812 5.76088 17.5447 5.74565C17.2981 5.73449 17.0806 5.77164 16.9302 5.85393C16.5408 6.06725 16.084 6.63972 15.8456 6.99892C15.7968 7.07282 15.7795 7.18857 15.7772 7.3077C15.7767 7.34581 15.7731 7.40253 15.7703 7.43667C15.546 7.48217 14.7236 7.5816 14.487 7.59738C14.4606 7.56738 14.4469 7.52038 14.4282 7.47182C14.3862 7.36186 14.3502 7.23489 14.2969 7.12279C14.2271 6.97663 14.1287 6.85481 13.9796 6.79016C13.841 6.72998 13.65 6.71613 13.3783 6.81018C13.1562 6.8869 13.1043 7.10792 13.148 7.39186C13.1681 7.52448 13.2045 7.6726 13.2077 7.80568C13.2096 7.88295 13.2059 7.95644 13.143 7.99811C12.8681 8.17979 12.5745 8.07452 12.2823 7.93497C11.9166 7.76031 11.5515 7.52357 11.2205 7.47893C10.9889 7.44775 10.7541 7.47889 10.5207 7.49694C10.3256 7.51203 10.1309 7.51851 9.94169 7.44173C9.80811 7.38753 9.69139 7.27907 9.57742 7.16532C9.44111 7.02956 9.31118 6.88453 9.1776 6.78009C9.14842 6.75743 8.87625 6.60853 8.73538 6.53222C8.75817 6.49096 8.78781 6.44824 8.81744 6.40393C8.89175 6.29478 8.97427 6.18127 9.02989 6.06998C9.13338 5.86309 9.14933 5.6614 8.99205 5.48164C8.94555 5.42849 8.86075 5.36648 8.75088 5.30165C8.5439 5.17966 8.2348 5.03404 7.99956 4.87051C7.90473 4.80445 7.82175 4.73684 7.77161 4.66467C7.74516 4.62734 7.72647 4.59059 7.7397 4.55074C7.76112 4.48851 7.80216 4.44279 7.84911 4.40212C7.91385 4.3455 7.98998 4.2985 8.06156 4.24816C8.18921 4.15835 8.30364 4.05764 8.36656 3.91677C8.40075 3.84073 8.40029 3.72543 8.36017 3.59404C8.3237 3.47414 8.25714 3.33267 8.20699 3.20904C8.18283 3.14927 8.1614 3.09611 8.15821 3.05613C8.15092 2.96212 8.20426 2.86985 8.26626 2.77342C8.3506 2.64263 8.45865 2.5106 8.54573 2.37802C8.67839 2.17551 8.75726 1.96808 8.70985 1.7653C8.67748 1.62689 8.58812 1.48373 8.39801 1.34642C8.18328 1.19178 7.94986 1.23559 7.70915 1.35544C7.57785 1.42086 7.44381 1.50863 7.30932 1.56648C7.21632 1.60646 7.12469 1.63555 7.03397 1.59908C6.69523 1.46244 6.12536 0.993825 5.80121 0.7682C5.55594 0.597239 4.97695 0.46129 4.53929 0.538564C4.19372 0.599609 3.93796 0.786664 3.90924 1.12562C3.88781 1.37842 3.99403 1.54305 4.12989 1.69418C4.21925 1.79393 4.32958 1.88237 4.3738 2.02156C4.40207 2.111 4.38474 2.26829 4.3738 2.45767C4.35328 2.80233 4.33504 3.23434 4.46634 3.60804C4.52287 3.76952 4.71116 3.94166 4.88076 4.11285C4.94413 4.17709 5.00385 4.24146 5.04169 4.30821C5.0622 4.34463 5.07816 4.38051 5.06768 4.41853C5.0344 4.53816 4.89763 4.66458 4.75994 4.79415C4.58625 4.95768 4.40252 5.11838 4.31316 5.25032C4.11393 5.54374 3.99814 5.90827 3.8486 6.25357C3.7173 6.55706 3.55911 6.84637 3.25912 7.03329C3.157 7.09694 3.03117 7.09402 2.89896 7.08002C2.72618 7.06174 2.54382 7.01834 2.37195 7.00102C2.15859 6.9795 1.9589 6.9986 1.79524 7.10528C1.63385 7.21023 1.49754 7.40316 1.43325 7.75931C1.40636 7.90848 1.45423 8.03595 1.54085 8.14897C1.62519 8.25866 1.75603 8.34952 1.82533 8.44676C1.99629 8.68793 2.29216 9.19963 2.39246 9.56681C2.41024 9.63223 2.4221 9.692 2.42255 9.74315ZM8.04925 3.78452C7.99728 3.89092 7.88832 3.95028 7.78802 4.01898C7.628 4.12867 7.47892 4.24958 7.41418 4.44046C7.34717 4.63869 7.43288 4.83933 7.63119 5.01781C7.8327 5.19972 8.15593 5.36698 8.41625 5.50831C8.5594 5.58586 8.6825 5.64978 8.73356 5.70804C8.76912 5.74857 8.76775 5.79371 8.75407 5.83957C8.7372 5.89487 8.7062 5.95076 8.67064 6.00725C8.54892 6.20201 8.39026 6.4011 8.3588 6.568C8.35242 6.60224 8.34923 6.67874 8.42719 6.74667C8.5056 6.81487 8.92549 7.01939 8.96606 7.05098C9.08961 7.14754 9.2086 7.28294 9.33443 7.4085C9.48214 7.55612 9.63942 7.68983 9.81266 7.76018C10.0529 7.85774 10.2991 7.85879 10.5467 7.83964C10.7564 7.82346 10.967 7.79159 11.1744 7.81958C11.4753 7.86011 11.8018 8.08651 12.1341 8.24516C12.5403 8.43901 12.9506 8.53726 13.3327 8.28483C13.4749 8.19055 13.5378 8.0494 13.5497 7.88236C13.5583 7.75772 13.536 7.61625 13.5118 7.47939C13.4954 7.38949 13.4785 7.30205 13.4749 7.22559C13.474 7.20147 13.4744 7.17886 13.4781 7.15844C13.4795 7.14868 13.4804 7.13852 13.4904 7.13505C13.6449 7.08157 13.7544 7.07095 13.8355 7.1025C13.9522 7.14768 13.9928 7.27209 14.0352 7.38962C14.0776 7.50775 14.1136 7.62747 14.1629 7.72015C14.2112 7.81024 14.2723 7.87898 14.3489 7.92061C14.3593 7.92622 14.4058 7.946 14.4852 7.94181C14.7259 7.92913 15.8068 7.79173 15.9276 7.75088C16.0234 7.71828 16.0867 7.6385 16.1086 7.51618C16.1205 7.45017 16.1191 7.3656 16.1214 7.28486C16.1228 7.2482 16.1168 7.21182 16.1323 7.18889C16.3457 6.86716 16.7464 6.34653 17.0952 6.15542C17.201 6.09752 17.3555 6.08115 17.5292 6.08899C17.8219 6.10226 18.1652 6.19189 18.4524 6.33477C18.7328 6.47427 18.968 6.65672 19.0045 6.88444C18.9904 6.8937 18.9553 6.91645 18.9302 6.92711C18.8158 6.97526 18.6803 7.00813 18.606 7.05112C18.2135 7.2788 17.635 7.82761 17.4686 8.24694C17.4248 8.35695 17.4389 8.48501 17.4886 8.61617C17.5342 8.73653 17.6158 8.86067 17.6336 8.96389C17.6427 9.01746 17.6208 9.09158 17.5949 9.17032C17.5438 9.32551 17.4626 9.49077 17.4143 9.58564C17.3947 9.62385 17.355 9.6315 17.3149 9.64094C17.2575 9.6543 17.195 9.66077 17.133 9.67071C16.9046 9.70714 16.6894 9.78291 16.6056 10.0937C16.5645 10.2442 16.586 10.3937 16.6407 10.5415C16.6849 10.6613 16.7523 10.7804 16.813 10.8984C16.8782 11.0248 16.9479 11.1486 16.9192 11.2724C16.9133 11.266 16.9078 11.2596 16.9028 11.2535C16.7733 11.0913 16.6366 10.8334 16.6083 10.761C16.5882 10.7097 16.5946 10.7227 16.58 10.6844C16.5727 10.6652 16.5654 10.646 16.5577 10.6269C16.5445 10.5948 16.5221 10.5502 16.4989 10.5245C16.4583 10.4798 16.4036 10.4493 16.3338 10.4366C16.2509 10.4213 16.1323 10.4374 16.0056 10.4623C15.9267 10.4779 15.8428 10.4959 15.7662 10.4994C15.7197 10.5016 15.6719 10.5121 15.655 10.4677C15.4562 9.93381 15.6623 9.76969 15.852 9.31484C15.8779 9.25279 15.872 9.20214 15.8579 9.16216C15.8451 9.12623 15.8228 9.09514 15.7895 9.07029C15.7558 9.0449 15.6965 9.02274 15.6204 9.02101C15.5533 9.01946 15.4581 9.03587 15.4043 9.02922C15.21 9.00519 15.0126 9.00834 14.8257 8.94757C14.7428 8.92071 14.6744 8.85981 14.6096 8.79434C14.5248 8.70858 14.4478 8.61257 14.3712 8.53612C14.2622 8.42729 14.1483 8.35663 14.0297 8.34614C13.9062 8.3352 13.7626 8.38038 13.6067 8.56101C13.4772 8.71118 13.4302 8.90777 13.4462 9.13622C13.4658 9.41792 13.5852 9.75533 13.7124 10.0945C13.8287 10.4044 13.9518 10.716 13.9896 10.9828C14.0092 11.1233 14.0078 11.2488 13.9486 11.3461C13.8323 11.5361 13.6399 11.6133 13.4448 11.6858C13.1918 11.7797 12.931 11.8635 12.7386 12.0462C12.6324 12.147 12.5946 12.2596 12.575 12.3786C12.5599 12.473 12.5745 12.5766 12.4924 12.6651C12.4145 12.7492 12.3192 12.8093 12.2244 12.8708C12.1058 12.9477 11.9882 13.026 11.8897 13.1317C11.1676 13.9042 10.507 14.7269 9.77574 15.4821C9.58153 15.6824 9.32987 15.7717 9.0887 15.8789C8.79738 16.008 8.51928 16.1582 8.32234 16.4643C8.26398 16.5553 8.25076 16.7089 8.28769 16.8934C8.32507 17.0783 8.40485 17.3052 8.46002 17.5072C8.49102 17.6187 8.51564 17.7206 8.51153 17.7976C8.50333 17.9502 8.44178 18.1033 8.38069 18.2585C8.30683 18.4453 8.23161 18.6344 8.20836 18.8241C8.19332 18.9497 8.22933 19.1161 8.26215 19.2775C8.27492 19.3399 8.28632 19.4014 8.28906 19.4578C8.29088 19.4913 8.29224 19.5221 8.28085 19.5469C8.20152 19.7204 8.02007 19.8641 7.82951 20.0011C7.60703 20.1615 7.3686 20.3093 7.21131 20.4579C7.0791 20.5824 6.96786 20.7877 6.83291 20.9532C6.74219 21.0643 6.64007 21.1602 6.48187 21.1333C6.21061 21.0874 6.07293 20.8632 5.97719 20.5935C5.79939 20.0912 5.77204 19.4293 5.61977 19.0654C5.48665 18.7482 5.20171 18.4988 5.04716 18.182C4.93683 17.9554 4.89534 17.7108 4.85523 17.4663C4.81009 17.1921 4.76678 16.9177 4.65874 16.6613C4.41392 16.0804 4.01455 15.6122 3.88462 14.9838C3.78067 14.4802 3.80711 14.0328 3.83994 13.5959C3.87459 13.131 3.91607 12.6772 3.83903 12.1859C3.83219 12.1422 3.76335 11.9052 3.69314 11.8064C3.64299 11.7363 3.58053 11.7071 3.53221 11.701C3.39361 11.6837 3.23542 11.7589 3.08634 11.8857C2.98878 11.9685 2.89213 12.0707 2.80231 12.1518C2.759 12.1911 2.71934 12.2257 2.68196 12.2425C2.6432 12.2598 2.59488 12.2562 2.54108 12.2474C2.46176 12.2343 2.37514 12.2047 2.2867 12.1668C1.9931 12.0414 1.68491 11.8263 1.54996 11.7275C1.4565 11.659 1.38493 11.6024 1.33524 11.5492C1.30834 11.5199 1.28782 11.4958 1.28372 11.467C1.27916 11.4381 1.29101 11.4087 1.30834 11.3724C1.3398 11.3063 1.39085 11.2305 1.45878 11.1366C1.51075 11.0653 1.52261 10.9633 1.45377 10.8483C1.40818 10.7728 1.31517 10.6786 1.21351 10.5914C1.14558 10.5331 1.074 10.4779 1.01884 10.4353C1.05896 10.3944 1.11777 10.3523 1.18114 10.3083C1.35712 10.1854 1.57595 10.0692 1.68947 10.0045C1.72184 9.98619 1.7615 9.9861 1.8039 9.98834C1.87365 9.99198 1.94888 10.008 2.02592 10.0248C2.22834 10.0689 2.43805 10.1108 2.60309 10.0631C2.6391 10.0527 2.67056 10.0309 2.6929 10.0009C2.77086 9.8967 2.78681 9.70645 2.7239 9.47627C2.61403 9.07399 2.29308 8.51195 2.10525 8.24772C2.04325 8.16023 1.93657 8.07571 1.84949 7.98188C1.80527 7.93419 1.75967 7.88655 1.77153 7.82031C1.81119 7.60116 1.87547 7.46932 1.97304 7.40002C2.03823 7.35352 2.11573 7.33911 2.19825 7.33683C2.30538 7.33382 2.41982 7.35183 2.53561 7.37143C2.71752 7.40225 2.9017 7.43685 3.06947 7.43024C3.20442 7.42486 3.32979 7.394 3.44057 7.32511C3.80392 7.09903 4.00497 6.75711 4.16408 6.39011C4.30495 6.06492 4.40981 5.71971 4.59764 5.44335C4.69429 5.30088 4.91723 5.12572 5.09503 4.94801C5.2432 4.79989 5.36082 4.6468 5.39866 4.5109C5.44972 4.32713 5.36857 4.1424 5.21812 3.97034C5.13515 3.8756 5.03257 3.78274 4.94276 3.69179C4.87574 3.62363 4.81374 3.56017 4.79049 3.4942C4.67469 3.16427 4.69885 2.78227 4.71663 2.47796C4.73122 2.23464 4.73806 2.03268 4.70159 1.91775C4.6519 1.7611 4.55206 1.64353 4.44765 1.53197C4.34918 1.42638 4.23657 1.33237 4.25162 1.15462C4.26621 0.979601 4.42075 0.908572 4.59901 0.877023C4.94732 0.815523 5.4096 0.914225 5.60518 1.05026C5.94574 1.28756 6.54934 1.77409 6.90586 1.91784C7.0563 1.97861 7.2104 1.96712 7.36495 1.91342C7.4885 1.8707 7.6125 1.79985 7.73468 1.73165C7.81629 1.68606 7.89653 1.6417 7.97585 1.61403C8.05244 1.58722 8.12721 1.57496 8.19651 1.6252C8.29954 1.69924 8.35789 1.76858 8.37522 1.84325C8.40211 1.96015 8.33464 2.07298 8.25805 2.18969C8.15137 2.35258 8.01369 2.51507 7.92434 2.67509C7.84775 2.81172 7.80535 2.94872 7.81538 3.08252C7.82495 3.20685 7.93299 3.42732 8.00594 3.61957C8.02828 3.67861 8.04606 3.76902 8.04925 3.78452Z"
                  fill="black"
                />
              </svg>
              <span>Free shipping across India</span>
            </div>
            <div className="extra-info-item">
              <svg
                width="18"
                height="16"
                viewBox="0 0 18 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.95824 0.666626L0.75 3.41663L3.95824 6.62496"
                  stroke="black"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M0.75 3.41663H11.2888C14.4435 3.41663 17.1226 5.99264 17.2456 9.14579C17.3755 12.4777 14.6224 15.3333 11.2888 15.3333H3.49927"
                  stroke="black"
                  stroke-width="0.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span>Easy returns applicable</span>
            </div>
          </div>
        </div>
      </div>
      <SizeChart isOpen={openDialog} onClose={handleClose} />
    </Fragment>
  );
};

export default ProductInfo;
